import { useTheme } from '@mui/material';
import { Box } from '@mui/material';
import { BankIDLogo, MitIDLogo } from '@resurs-internal/mui';
import * as Sentry from '@sentry/react';
import { useState } from 'react';

import NorwegianBankId from '../../assets/svg/NorwegianBankId';
import { usePagesContext } from '../../context/PagesContext';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { WizardPageAction as PageAction } from '../../store/wizard/wizard';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';

type PagesActionButtonProps = { action: PageAction; onAction: (action: PageAction) => Promise<boolean> };

const norwegianBankIdStyles = {
    display: 'inline-flex',
    transform: 'translate(5px, 0)',
    paddingLeft: '8px',
    width: '24px',
    height: '24px',
};

const PagesActionButton = ({ action, onAction }: PagesActionButtonProps) => {
    const theme = useTheme();
    const { setPageAction } = usePagesContext();
    const [isLoading, setIsLoading] = useState(false);
    const [isNextDisabled, _setNextDisabled] = useState(false);
    const translate = useTranslation();
    const { countryCode } = useSettings();

    const handleAction = async () => {
        try {
            setIsLoading(true);
            setPageAction(action);
            await onAction(action);
        } catch (error) {
            // Pages set their own error state before rethrowing, and the rethrow is what stops the flow carrying on.
            // Without this catch it became an unhandled rejection: Button drops the promise, so only Sentry's global
            // handler ever saw it, and only in production. Reported explicitly so nothing is lost, and so these
            // error paths can be tested at all.
            Sentry.captureException(error, { tags: { source: 'pages_action_button' }, extra: { action } });
        } finally {
            setIsLoading(false);
        }
    };

    const createComponent = (action: PageAction) => {
        switch (action) {
            case 'back':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.back')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'next':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.next')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'submit':
                return (
                    <Button
                        onClick={handleAction}
                        fill
                        disabled={isLoading}
                        displayAsDisabled={isNextDisabled}
                        testId="action-submit">
                        {translate('page.action.submit')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'continue-with-coapplicant':
                return (
                    <Button
                        onClick={handleAction}
                        fill
                        disabled={isLoading}
                        displayAsDisabled={isNextDisabled}
                        testId="continue-with-coapplicant">
                        {translate('page.action.continue-with-coapplicant')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'submit-coapplicant':
                return (
                    <Button
                        onClick={handleAction}
                        fill
                        disabled={isLoading}
                        displayAsDisabled={isNextDisabled}
                        testId="action-co-submit">
                        {translate('page.action.submit-coapplicant')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'collect-details':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.verify-income')}
                        {countryCode === 'SE' && (
                            <BankIDLogo
                                size="sm"
                                color={theme.palette.primary.contrastText}
                                style={{ marginLeft: '12px' }}
                            />
                        )}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'collect-norskgjeld':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.norsk-gjeld')}
                        {countryCode === 'SE' && (
                            <BankIDLogo
                                size="sm"
                                color={theme.palette.primary.contrastText}
                                style={{ marginLeft: '12px' }}
                            />
                        )}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'collect-eskat-kreditstatus':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.eskat-kreditstatus')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'collect-montio':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.montio')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'mitid-login':
                return (
                    <Button
                        onClick={handleAction}
                        fill
                        disabled={isLoading}
                        displayAsDisabled={isNextDisabled}
                        testId="action-mitid-login">
                        {translate('page.action.mitid-login')}
                        {countryCode === 'DK' && (
                            <MitIDLogo
                                size="lg"
                                color={theme.palette.primary.contrastText}
                                style={{ marginLeft: '12px' }}
                            />
                        )}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'sign':
                return (
                    <Button
                        onClick={handleAction}
                        fill
                        disabled={isLoading}
                        displayAsDisabled={isNextDisabled}
                        testId="action-sign">
                        {translate('page.action.sign')}
                        {countryCode === 'NO' && (
                            <Box sx={norwegianBankIdStyles}>
                                <NorwegianBankId />
                            </Box>
                        )}
                        {countryCode === 'DK' && (
                            <MitIDLogo
                                size="lg"
                                color={theme.palette.primary.contrastText}
                                style={{ marginLeft: '12px' }}
                            />
                        )}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'redirect':
                return (
                    <Button
                        onClick={handleAction}
                        fill
                        disabled={isLoading}
                        displayAsDisabled={isNextDisabled}
                        testId="action-redirect">
                        {translate('page.action.redirect')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'consolidation-next':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.consolidation-next')}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );

            case 'consent':
                return (
                    <Button onClick={handleAction} fill disabled={isLoading} displayAsDisabled={isNextDisabled}>
                        {translate('page.action.consent')}
                        {countryCode === 'SE' && (
                            <BankIDLogo
                                size="sm"
                                color={theme.palette.primary.contrastText}
                                style={{ marginLeft: '12px' }}
                            />
                        )}
                        {isLoading && <Spinner size="sm" buttonLoadingStyles={true} />}
                    </Button>
                );
        }
    };

    return createComponent(action);
};

export default PagesActionButton;
